import React from 'react'
import { isEmpty } from 'lodash'
import Bus from '../../bus'
import { getBetItemsForMulti, getIntUID, getUniqueCount, logger } from '../../utils'

import { BETSLIP_SETTINGS_UPDATE, BETSLIP_STATE_UPDATE } from '../../actions'

import SubscriptionManager from '../../connection/subscription'
import BetManager from '../../managers/bet'
import { BETSLIP_ERROR_CODE, BETSLIP_VIOLATION } from '../../error'

import { useBetsOddsHighlight, useErrorsOddsHighlight } from './odds.highlighter'

import { BetslipContext } from './betslip.context'
import { betslipReducer } from './betslip.reducer'
import { emptyBetslipState } from './betslip.constants'
import { BONUS_TYPE } from '../../constants'

export const BetslipProvider = (props) => {
  const { children, id } = props

  const betslipState = createBetslipState({
    id
  })

  return <BetslipContext.Provider value={betslipState}>{children}</BetslipContext.Provider>
}

const createBetslipInitialState = (options) => {
  const {
    id = 'main',
    ...restOptions
  } = options

  return {
    ...emptyBetslipState,
    id,
    loading: true,
    initialized: false,
    ...restOptions
  }
}

export function createBetslipState (options) {
  const [state, dispatch] = React.useReducer(
    betslipReducer,
    options,
    createBetslipInitialState
  )

  /*
    Initializing
    - Register betslip dispatcher in bus
    - Resolving stored bet items
  */
  React.useEffect(
    () => {
      // Once settings and stored bets resolved - we can set initialization flag to true
      Promise.all([
        BetManager.getBetslipSettings(),
        BetManager.getBetslipInitialOptions()
      ])
        .then(([settings, options]) => {
          const stateUpdate = {
            betItems: options.betItems.map(bet => {
              bet.requestId = bet.requestId || getIntUID()
              return bet
            }),
            loading: options.loading,
            settings,
            initialized: true
          }

          stateUpdate.betTypes = BetManager.betTypesCalculator.getAvailableBetTypes(
            stateUpdate
          )

          dispatch({
            type: BETSLIP_STATE_UPDATE,
            payload: stateUpdate
          })

          if (options?.betReports?.length) {
            Bus.emit(Bus.events.bet.placedResult, {
              data: {
                placeBetRequestId: '',
                bets: options?.betReports
              }
            })
          }
        })
        .catch((error) => {
          logger.debug(`[betslip:${state.id}]`, 'initializing betslip with error', error)
          // Trying initialize without settings
          dispatch({
            type: BETSLIP_STATE_UPDATE,
            payload: {
              error,
              initialized: true,
              loading: false
            }
          })
        })

      // Registering betslip dispatch
      const handleBetslipDispatch = action => dispatch(action)

      // Using betslip id
      Bus.on(`betslip:${state.id}`, handleBetslipDispatch)

      return () => {
        logger.debug(`[betslip:${state.id}]`, 'unmounted')
        BetManager.resetBetslipInitialOptions()
        Bus.off(`betslip:${state.id}`, handleBetslipDispatch)
      }
    },
    []
  )

  /*
    Subscribing on provider status change updates
  */
  React.useEffect(
    () => {
      const betslipSubscriptionTopics = [
        {
          topic: SubscriptionManager.subscriptionTopics.GLOBAL_STATUS_UPDATES,
          key: ''
        }
      ]

      SubscriptionManager.notifierSubscribe(betslipSubscriptionTopics)

      const handleProviderStatusUpdate = ({ data }) => {
        const { feedName, status } = data

        logger.info(`[betslip:${state.id}]`, '⚙️', `provider "${feedName}" status changed to "${status}"`)

        dispatch({
          type: BETSLIP_SETTINGS_UPDATE,
          payload: {
            feedStates: {
              [feedName]: status
            }
          }
        })
      }

      Bus.on(Bus.events.global.providerStatusUpdate, handleProviderStatusUpdate)

      return () => {
        SubscriptionManager.notifierUnsubscribe(betslipSubscriptionTopics)
        Bus.off(Bus.events.global.providerStatusUpdate, handleProviderStatusUpdate)
      }
    },
    []
  )

  React.useEffect(() => {
    // multi bets with different events and not exotic market
    const betItemsForMulti = getBetItemsForMulti(state.betItems)
    const isPossibleMultiNew = betItemsForMulti.length > 1 &&
      getUniqueCount(betItemsForMulti, 'eventId') === betItemsForMulti.length
    // && getUniqueCount(betItems, 'betEventType') === 1;

    if (state.isPossibleMulti !== isPossibleMultiNew) {
      dispatch({
        type: BETSLIP_STATE_UPDATE,
        payload: {
          isPossibleMulti: isPossibleMultiNew,
          amount: null
        }
      })
    }
  }, [state.isPossibleMulti, state.betItems])

  React.useEffect(() => {
    if (!isEmpty(state.outcomesErrors)) {
      // update changed odds
      const stateUpdate = {
        betItems: state.betItems.map(bet => {
          const outcomeError = state.outcomesErrors[bet.outcomeId]
          let odds = bet.odds
          let oddsForMulti = bet.oddsForMulti
          if (outcomeError?.code === BETSLIP_VIOLATION.OUTCOME_ODDS_CHANGED && outcomeError?.violationInfo?.newOdds) {
            odds = outcomeError?.violationInfo?.newOdds
            oddsForMulti = outcomeError?.violationInfo?.newOddsForMulti
          }
          return ({ ...bet, odds, oddsForMulti })
        })
      }

      stateUpdate.betTypes = BetManager.betTypesCalculator.getAvailableBetTypes({
        ...state, ...stateUpdate
      })

      dispatch({
        type: BETSLIP_STATE_UPDATE,
        payload: stateUpdate
      })

      logger.info(`[betslip:${state.id}]`, '⚙️', 'updated bets with new odds: ', stateUpdate)
    }
  }, [state.outcomesErrors])

  React.useEffect(() => {
    if (!isEmpty(state.errors)) {
      // bets expired, event closed
      const inactiveOutcomeIds = state.errors
        .map(betError => {
          const violation = betError?.violations.find(item => item.code === BETSLIP_ERROR_CODE.OUTCOME_INACTIVE)
          return violation ? violation.violationInfo.outcomeId : null
        })
        .filter(Boolean)
        .map(String) // make sure outcomeId from API response is consistently treated as a string

      const stateUpdate = {
        betItems: state.betItems.map(bet => {
          // compare strings. a part of AP-1948 was [string].includes(int) -> false for the "same" outcome ids of different types
          if (inactiveOutcomeIds.includes(String(bet.outcomeId))) {
            return {
              ...bet,
              active: false,
              suspended: true
            }
          }
          return bet
        })
      }

      dispatch({
        type: BETSLIP_STATE_UPDATE,
        payload: stateUpdate
      })

      logger.info(`[betslip:${state.id}]`, '⚙️', 'updated bets with inactive outcomes: ', stateUpdate)
    }
  }, [state.errors])

  // Automatically assign PROMO_ODDS bonus
  React.useEffect(() => {
    const { betItems, bonuses, bonusAssignments } = state

    if (Array.isArray(betItems) && betItems.length > 0 && bonuses) {
      const promoOddsByRequestId = Object.entries(bonuses)
        .reduce((subResult, [requestId, requestIdBonuses]) => {
          const promoOdds = requestIdBonuses.find(b => b.bonusType === BONUS_TYPE.PROMO_ODDS)
          return promoOdds ? { ...subResult, [requestId]: promoOdds } : subResult
        }, {})

      const betItemRequestIdsWithoutBonus = betItems
        .filter(betItem => (bonusAssignments || []).every(({ requestId }) => requestId !== betItem.requestId))
        .map(({ requestId }) => requestId)

      const requestIdsToAssignPromoOdds = Object.keys(promoOddsByRequestId)
        .filter(requestId => betItemRequestIdsWithoutBonus.includes(parseInt(requestId, 10)))

      if (requestIdsToAssignPromoOdds.length > 0) {
        dispatch({
          type: BETSLIP_STATE_UPDATE,
          payload: {
            bonusAssignments: [
              ...(bonusAssignments || []),
              ...requestIdsToAssignPromoOdds.map(requestId => ({
                requestId: parseInt(requestId, 10),
                bonusId: promoOddsByRequestId[requestId].id
              }))
            ]
          }
        })
      }
    }
  }, [state.betItems, state.bonuses, state.bonusAssignments])

  useBetsOddsHighlight(state.betItems)
  useErrorsOddsHighlight(state.errors)

  return state
}
